import React, { useEffect, useRef, useCallback } from "react";
import { cn } from "../lib/utils";
import { createNoise3D } from "simplex-noise";
import { motion } from "motion/react";

export const Vortex = ({
  className,
  containerClassName,
  children,
  particleCount = 700,
  rangeY = 100,
  baseSpeed = 0.0,
  rangeSpeed = 1.5,
  baseRadius = 1,
  rangeRadius = 2,
  baseHue = 220,
}) => {
  const canvasRef = useRef(null);
  const animationFrameId = useRef(null);

  const particlePropCount = 9;
  const particlePropsLength = particleCount * particlePropCount;

  const particleProps = useRef(new Float32Array(particlePropsLength));
  const center = useRef([0, 0]);
  const tickRef = useRef(0);
  const noise3DRef = useRef(createNoise3D());

  const baseTTL = 50;
  const rangeTTL = 150;
  const rangeHue = 100;

  const xOff = 0.00125;
  const yOff = 0.00125;
  const zOff = 0.0005;
  const noiseSteps = 3;

  /* -------------------- Utils -------------------- */

  const lerp = (a, b, t) => (1 - t) * a + t * b;

  const fadeInOut = (t, m) => {
    const hm = 0.5 * m;
    return Math.abs(((t + hm) % m) - hm) / hm;
  };

  /* -------------------- Canvas Resize -------------------- */

  const resize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const parent = canvas.parentElement;
    if (!parent) return;

    const rect = parent.getBoundingClientRect();

    canvas.width = rect.width;
    canvas.height = rect.height;

    center.current[0] = rect.width * 0.5;
    center.current[1] = rect.height * 0.5;
  }, []);


  /* -------------------- Particle Init -------------------- */

  const initParticle = useCallback(
    (i) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const randRange = (n) => n - Math.random() * 2 * n;

      const x = Math.random() * canvas.width;
      const y = center.current[1] + randRange(rangeY);

      particleProps.current.set(
        [
          x,
          y,
          0,
          0,
          0,
          baseTTL + Math.random() * rangeTTL,
          baseSpeed + Math.random() * rangeSpeed,
          baseRadius + Math.random() * rangeRadius,
          baseHue + Math.random() * rangeHue,
        ],
        i
      );
    },
    [rangeY, baseSpeed, rangeSpeed, baseRadius, rangeRadius, baseHue]
  );

  const initParticles = useCallback(() => {
    tickRef.current = 0;
    particleProps.current = new Float32Array(particlePropsLength);

    for (let i = 0; i < particlePropsLength; i += particlePropCount) {
      initParticle(i);
    }
  }, [initParticle, particlePropsLength]);

  /* -------------------- Drawing -------------------- */

  const drawParticle = useCallback(
    (x, y, x2, y2, life, ttl, radius, hue, ctx) => {
      ctx.save();
      ctx.lineCap = "round";
      ctx.lineWidth = radius;
      ctx.strokeStyle = `hsla(${hue},100%,60%,${fadeInOut(life, ttl)})`;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      ctx.restore();
    },
    []
  );

  const getResolvedBgColor = () => {
  const styles = getComputedStyle(document.documentElement);
  return styles.getPropertyValue("--bg-primary").trim() || "#000";
  };

  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    tickRef.current++;

    ctx.fillStyle = getResolvedBgColor();
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const p = particleProps.current;

    for (let i = 0; i < particlePropsLength; i += particlePropCount) {
      const x = p[i];
      const y = p[i + 1];
      const life = p[i + 4];
      const ttl = p[i + 5];
      const speed = p[i + 6];
      const radius = p[i + 7];
      const hue = p[i + 8];

      const n =
        noise3DRef.current(
          x * xOff,
          y * yOff,
          tickRef.current * zOff
        ) *
        noiseSteps *
        Math.PI *
        2;

      const vx = lerp(p[i + 2], Math.cos(n), 0.5);
      const vy = lerp(p[i + 3], Math.sin(n), 0.5);

      const x2 = x + vx * speed;
      const y2 = y + vy * speed;

      drawParticle(x, y, x2, y2, life, ttl, radius, hue, ctx);

      p[i] = x2;
      p[i + 1] = y2;
      p[i + 2] = vx;
      p[i + 3] = vy;
      p[i + 4] = life + 1;

      if (
        x2 < 0 ||
        x2 > canvas.width ||
        y2 < 0 ||
        y2 > canvas.height ||
        life > ttl
      ) {
        initParticle(i);
      }
    }

    animationFrameId.current = requestAnimationFrame(animate);
  }, [
    particlePropsLength,
    drawParticle,
    initParticle,
  ]);

  /* -------------------- Lifecycle -------------------- */

  useEffect(() => {
    resize();
    initParticles();
    animate();

    window.addEventListener("resize", resize);

    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(animationFrameId.current);
    };
  }, [resize, initParticles, animate]);

  /* -------------------- Render -------------------- */

  return (
    <div className={cn("relative h-full w-full", containerClassName)}>
      <motion.canvas
        ref={canvasRef}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="absolute inset-0 z-0"
      />
      <div className={cn("relative z-10", className)}>
        {children}
      </div>
    </div>
  );
};